import React from 'react';
import {
  User,
  LogOut,
  Shield,
  FolderKanban,
  Sparkles,
  CalendarClock,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';

interface AuthUser {
  id: number;
  username: string;
  role: string;
  isActive?: boolean;
  createdAt?: string | null;
  lastLoginAt?: string | null;
}

interface MyPageProps {
  currentUser: AuthUser | null;
  onLogout: () => void;
  onGoDeveloper?: () => void;
  onGoContact?: () => void;
}

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '未記録';
  }
  const normalized = /z$|[+-]\d{2}:\d{2}$/i.test(value) ? value : `${value}Z`;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return '未記録';
  }
  return parsed.toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Tokyo',
  });
};

const MyPage: React.FC<MyPageProps> = ({ currentUser, onLogout, onGoDeveloper, onGoContact }) => {
  const isAdmin = currentUser?.role === 'admin';
  const roleLabel = isAdmin ? '管理者アカウント' : '閲覧ユーザー';
  const statusLabel = currentUser?.isActive === false ? '停止中' : '有効';
  const capabilityTitle = isAdmin ? '配信ファイルを管理できます' : '共有ファイルを分析できます';
  const capabilityDescription = isAdmin
    ? '管理者コンソールから Excel / CSV を登録し、ユーザー向けの共有ファイルを更新できます。'
    : '管理者が公開したファイルを選択し、分析画面とチャットで内容を確認できます。';
  const capabilityPoints = isAdmin
    ? [
        'アップロード、名称変更、削除は管理者コンソールから実行',
        '登録したファイルは一般ユーザーの共有ライブラリへ公開',
        '共有前に内容を確認して更新タイミングを管理',
      ]
    : [
        '共有ファイルライブラリから対象ファイルを選択',
        '分析画面で集計とグラフを確認',
        'アップロードや更新が必要な場合は管理者へ依頼',
      ];

  return (
    <div className="account-page">
      <section className="account-hero-card">
        <div className="account-hero-main">
          <div className="account-avatar-shell">
            <div className="account-avatar">
              <User size={34} />
            </div>
          </div>
          <div className="account-hero-copy">
            <span className="account-eyebrow">Account Overview</span>
            <h2>{currentUser?.username || '未ログイン'}</h2>
            <p>{capabilityDescription}</p>
            <div className="account-badges">
              <span className={`account-badge ${isAdmin ? 'role-admin' : 'role-user'}`}>{roleLabel}</span>
              <span className={`account-badge ${currentUser?.isActive === false ? 'status-inactive' : 'status-active'}`}>{statusLabel}</span>
            </div>
          </div>
        </div>

        <div className="account-hero-actions">
          {onGoContact && (
            <button className="account-action-button primary" onClick={onGoContact}>
              お問い合わせへ
              <ArrowRight size={16} />
            </button>
          )}
          {onGoDeveloper && (
            <button className="account-action-button primary" onClick={onGoDeveloper}>
              <FolderKanban size={18} />
              管理者画面へ
              <ArrowRight size={16} />
            </button>
          )}
          <button className="account-action-button secondary" onClick={onLogout}>
            <LogOut size={18} />
            ログアウト
          </button>
        </div>
      </section>

      <section className="account-grid">
        <article className="account-panel account-panel-emphasis">
          <div className="account-panel-header">
            <div className="account-panel-icon">
              <Sparkles size={20} />
            </div>
            <div>
              <h3>{capabilityTitle}</h3>
              <p>{isAdmin ? '公開フローの起点となる管理者向け導線です。' : '閲覧ユーザー向けの利用範囲を整理しています。'}</p>
            </div>
          </div>
          <ul className="account-check-list">
            {capabilityPoints.map((point) => (
              <li key={point}>
                <CheckCircle2 size={16} />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </article>

        <article className="account-panel">
          <div className="account-panel-header">
            <div className="account-panel-icon">
              <Shield size={20} />
            </div>
            <div>
              <h3>アカウント情報</h3>
              <p>日常利用で必要な情報だけをまとめています。</p>
            </div>
          </div>
          <dl className="account-info-grid">
            <div className="account-info-item">
              <dt>ユーザー名</dt>
              <dd>{currentUser?.username || '-'}</dd>
            </div>
            <div className="account-info-item">
              <dt>権限</dt>
              <dd>{roleLabel}</dd>
            </div>
            <div className="account-info-item">
              <dt>アカウント状態</dt>
              <dd>{statusLabel}</dd>
            </div>
          </dl>
        </article>

        <article className="account-panel">
          <div className="account-panel-header">
            <div className="account-panel-icon">
              <CalendarClock size={20} />
            </div>
            <div>
              <h3>利用メモ</h3>
              <p>次の行動が分かるように要点だけを残しています。</p>
            </div>
          </div>
          <dl className="account-info-grid single-column">
            <div className="account-info-item">
              <dt>最終ログイン</dt>
              <dd>{formatDateTime(currentUser?.lastLoginAt)}</dd>
            </div>
            <div className="account-info-item">
              <dt>利用範囲</dt>
              <dd>{isAdmin ? '共有ファイルの登録・更新・分析' : '共有ファイルの閲覧・分析'}</dd>
            </div>
            <div className="account-info-item">
              <dt>アップロード</dt>
              <dd>{isAdmin ? '管理者コンソールから実行' : '管理者のみ実行可能'}</dd>
            </div>
          </dl>
        </article>
      </section>
    </div>
  );
};

export default MyPage;
